/**
 * Generate scope label from DashboardScope (workflow `/dashboard` + personal dashboard).
 */

import type { DashboardScope } from '../types'

const DATE_LABELS: Record<string, string> = {
  last_7d: 'Last 7d',
  last_30d: 'Last 30d',
  last_60d: 'Last 60d',
  last_90d: 'Last 90d',
  last_quarter: 'Last Quarter',
  last_year: 'Last Year',
  custom: 'Custom',
}

function dateLabel(scope: DashboardScope): string {
  return DATE_LABELS[scope.dateRange] ?? scope.dateRange
}

function isPortfolioScope(scope: DashboardScope): boolean {
  return (
    !scope.products?.length &&
    !scope.productGroups?.length &&
    !scope.nodes?.length &&
    !scope.businessUnits?.length &&
    !scope.foundries?.length &&
    !scope.testStages?.length &&
    !scope.customers?.length &&
    !scope.workflows?.length
  )
}

export function generateScopeLabel(scope: DashboardScope): string {
  const datePart = dateLabel(scope)

  if (isPortfolioScope(scope)) {
    return `Portfolio • ${datePart}`
  }

  const parts: string[] = []

  if (scope.products && scope.products.length > 0) {
    parts.push(scope.products.length === 1 ? scope.products[0] : `${scope.products.length} products`)
  } else if (scope.productGroups && scope.productGroups.length > 0) {
    parts.push(scope.productGroups.join(', '))
  } else {
    parts.push('All products')
  }

  if (scope.nodes && scope.nodes.length > 0) {
    parts.push(scope.nodes.join(', '))
  } else {
    parts.push('All nodes')
  }

  if (scope.businessUnits && scope.businessUnits.length > 0) {
    parts.push(scope.businessUnits.join(', '))
  }

  if (scope.foundries && scope.foundries.length > 0) {
    parts.push(scope.foundries.join(', '))
  }

  if (scope.testStages && scope.testStages.length > 0) {
    parts.push(scope.testStages.join(', '))
  }

  if (scope.customers && scope.customers.length > 0) {
    parts.push(scope.customers.length === 1 ? scope.customers[0] : `${scope.customers.length} customers`)
  }

  if (scope.workflows && scope.workflows.length > 0) {
    parts.push(scope.workflows.length === 1 ? scope.workflows[0] : `${scope.workflows.length} workflows`)
  } else {
    parts.push('All workflows')
  }

  parts.push(datePart)

  return parts.join(' • ')
}
